<script setup lang="ts">
  import { getCurrentInstance, onMounted, onUnmounted } from "vue";
  import { storeToRefs } from "pinia";
  import { useRouter } from "vue-router";

  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import EmailCampaignsTable from "@/components/Mailing/EmailCampaignsTable.vue";
  import { useEmailCampaignsStore } from "@stores/survey/mailing/emailCampaigns";
  import { useEmailCampaignsFilters } from "@utils/composables/filters/useEmailCampaignsFilters";
  import type { Survey } from "@/types";

  const props = defineProps<{
    survey: Survey;
  }>();

  const router = useRouter();
  const emailCampaignsStore = useEmailCampaignsStore();
  const { emailCampaignsSearchQuery } = storeToRefs(emailCampaignsStore);
  const { resetAllFilters } = emailCampaignsStore;

  const { filterConfigs, activeFiltersCount, handleFilterChange, handleClear } = useEmailCampaignsFilters();

  const navigateToNewCampaign = () => {
    router.push(`/surveys/${props.survey.token}/mailing/campaigns/new`);
  };

  const instance = getCurrentInstance();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped ActionCable Vue plugin
  const cable = (instance?.proxy as any)?.$cable;

  onMounted(() => {
    if (cable) {
      cable.subscribe({ channel: "BrevoChannel", survey_token: props.survey.token });
      cable._addChannel("BrevoChannel", emailCampaignsStore.brevoChannelHandlers(cable, props.survey.token), {
        _uid: instance?.uid,
        _value: instance?.proxy,
      });
    }
  });

  onUnmounted(() => resetAllFilters());
</script>

<template>
  <IndexPageLayout
    title="Email campaigns"
    data-cy="survey-email-campaigns-page"
    action-label="New campaign"
    @action="navigateToNewCampaign">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="emailCampaignsSearchQuery"
        search-placeholder="Search campaigns">
        <template #filters>
          <FilterDropdown
            :filters="filterConfigs"
            :active-filters-count="activeFiltersCount"
            @toggle="handleFilterChange"
            @clear="handleClear" />
        </template>
      </IndexPageFilters>
    </template>

    <EmailCampaignsTable />
  </IndexPageLayout>
</template>
